import { useState, useEffect, useRef } from 'react';
import {
  trackAssessmentStarted,
  trackAssessmentStepCompleted,
  trackAssessmentCompleted,
} from '@shared/analytics';
import type {
  AssessmentConfig,
  AssessmentWizardState,
  AccountData,
  FotoData,
  MeterkastData,
  WoningData,
  ApparatenData,
  LaadwensenData,
} from '@shared/types';
import { uploadFoto } from '@shared/hooks/useAjax';
import WizardLayout from './WizardLayout';
import StapAccount from './StapAccount';
import StapFoto from './StapFoto';
import StapHandmatig from './StapHandmatig';
import StapWoning from './StapWoning';
import StapApparaten from './StapApparaten';
import StapLaadwensen from './StapLaadwensen';
import StapSamenvatting from './StapSamenvatting';

const LS_WIZARD_KEY = 'laadcheck_wizard_state';

interface Props {
  config: AssessmentConfig;
}

// ---- Default state factories ----

function defaultAccount(): AccountData {
  return { email: '', voornaam: '', achternaam: '', akkoordAV: false };
}

function defaultFoto(): FotoData {
  return { uploadId: null, aiAnalyse: null, gebruikHandmatig: false };
}

function defaultMeterkast(): MeterkastData {
  return {
    aansluiting: null,
    hoofdzekering: null,
    groepenkastType: null,
    aantalGroepen: 8,
    aantalVrijeGroepen: 0,
    heeftAardlek: null,
    heeftSlimmeMeter: null,
  };
}

function defaultWoning(): WoningData {
  return { type: null, typeAnders: null, oppervlakte: null, bouwjaar: null };
}

function defaultApparaten(): ApparatenData {
  return {
    inductiePlaat: false, inductieVermogen: null,
    keramischePlaat: false,
    elektrischeOven: false,
    elektrischeBoiler: false, boilerInhoud: null,
    warmtepomp: false, warmtepompType: null, warmtepompVermogen: null,
    airconditioning: false, aircoUnits: null,
    wasmachine: false, wasdroger: false, vaatwasser: false,
    zonnepanelen: false, zonnepanelenVermogen: null,
    thuisbatterij: false, thuisbatterijCapaciteit: null,
    sauna: false, jacuzzi: false, zwembad: false, thuiskantoor: false,
    opmerkingen: '',
  };
}

function defaultLaadwensen(): LaadwensenData {
  return {
    dlbInteresse: null,
    installatietermijn: null,
    isVVE: null,
    vveAantalParkeerplekken: null,
  };
}

function buildInitialState(): AssessmentWizardState {
  // Order token from WooCommerce redirect — always read from URL so it survives refreshes.
  let urlToken = '';
  let prefillEmail = '';
  let prefillVoornaam = '';
  try {
    const params = new URLSearchParams(window.location.search);
    urlToken = params.get('token') ?? '';
    if (params.get('email')) prefillEmail = params.get('email')!;
    if (params.get('naam')) prefillVoornaam = params.get('naam')!;
  } catch {
    // ignore
  }

  // Try to restore in-progress wizard from localStorage
  try {
    const saved = localStorage.getItem(LS_WIZARD_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as AssessmentWizardState;
      // Never restore a stale assessmentNonce — WordPress nonces expire after 24h.
      if (parsed && typeof parsed.huidigeStap === 'number') {
        // URL token always takes precedence over stale localStorage token.
        return { ...parsed, assessmentNonce: '', orderToken: urlToken || parsed.orderToken || '' };
      }
    }
  } catch {
    // ignore
  }

  // Pre-fill account from calculator lead gate (A3) or URL params
  try {
    if (!prefillEmail) prefillEmail = localStorage.getItem('laadcheck_lead_email') ?? '';
    if (!prefillVoornaam) prefillVoornaam = localStorage.getItem('laadcheck_lead_voornaam') ?? '';
  } catch {
    // ignore
  }

  return {
    huidigeStap: 1,
    assessmentId: null,
    assessmentNonce: '',
    securityKey: '',
    orderToken: urlToken,
    account: { ...defaultAccount(), email: prefillEmail, voornaam: prefillVoornaam },
    foto: defaultFoto(),
    meterkast: defaultMeterkast(),
    woning: defaultWoning(),
    apparaten: defaultApparaten(),
    laadwensen: defaultLaadwensen(),
  };
}

// ---- AJAX helper ----

async function ajaxPost<T>(
  ajaxUrl: string,
  nonce: string,
  action: string,
  params: Record<string, unknown>
): Promise<T> {
  const body = new FormData();
  body.append('action', action);
  body.append('nonce', nonce);
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined) {
      body.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
    }
  }
  const res = await fetch(ajaxUrl, { method: 'POST', body });
  const json = await res.json();
  if (!json.success) throw new Error((json.data as { message: string })?.message ?? 'AJAX fout');
  return json.data as T;
}

// ---- Main component ----

export default function App({ config }: Props) {
  const [wizardState, setWizardState] = useState<AssessmentWizardState>(buildInitialState);
  const [isLoading, setIsLoading] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFout, setUploadFout] = useState<string | null>(null);
  // A1: track when user navigated to a step via "Wijzig" in samenvatting
  const [returnToSamenvatting, setReturnToSamenvatting] = useState(false);

  const { huidigeStap, foto } = wizardState;
  const toonHandmatig = foto.gebruikHandmatig && foto.uploadId === null;

  // Track assessment opened (once on mount)
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const source = localStorage.getItem('laadcheck_lead_email') ? 'calculator_cta' : 'direct';
    trackAssessmentStarted(source as 'calculator_cta' | 'direct');
  }, []);

  // Persist wizard state to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(LS_WIZARD_KEY, JSON.stringify(wizardState));
    } catch {
      // ignore
    }
  }, [wizardState]);

  function updateStap<K extends keyof AssessmentWizardState>(
    key: K,
    value: AssessmentWizardState[K]
  ) {
    setWizardState((prev) => ({ ...prev, [key]: value }));
  }

  function goTo(stap: number) {
    setWizardState((prev) => ({ ...prev, huidigeStap: stap }));
    setFout(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // A1: navigate to a step from the samenvatting (sets returnToSamenvatting flag)
  function goToFromSamenvatting(stap: number) {
    setReturnToSamenvatting(true);
    goTo(stap);
  }

  // A1: after saving a step edit, return to samenvatting
  function afterStepEdit(defaultNextStap: number) {
    if (returnToSamenvatting) {
      setReturnToSamenvatting(false);
      goTo(6);
    } else {
      goTo(defaultNextStap);
    }
  }

  // A1: "Terug naar samenvatting" from any edit step
  function handleReturnToSamenvatting() {
    setReturnToSamenvatting(false);
    goTo(6);
  }

  // Step 1: create assessment record
  async function handleAccountNext() {
    setIsLoading(true);
    setFout(null);
    try {
      const data = await ajaxPost<{ assessment_id: number; nonce: string; security_key: string }>(
        config.ajaxUrl,
        config.nonce,
        'laadcheck_create_assessment',
        {
          email: wizardState.account.email,
          voornaam: wizardState.account.voornaam,
          achternaam: wizardState.account.achternaam,
        }
      );
      setWizardState((prev) => ({
        ...prev,
        assessmentId: data.assessment_id,
        assessmentNonce: data.nonce,
        securityKey: data.security_key,
        huidigeStap: 2,
      }));
    } catch (e) {
      setFout((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  }

  // Step 2: upload and analyse photo
  async function handleFotoUpload(file: File) {
    if (!wizardState.assessmentId) return;
    setIsUploading(true);
    setUploadFout(null);
    try {
      const res = await uploadFoto(
        config.ajaxUrl,
        wizardState.assessmentNonce || config.nonce,
        'laadcheck_upload_foto',
        file,
        { assessment_id: wizardState.assessmentId, foto_type: 'meterkast' }
      );
      if (!res.success) {
        const errData = (res as { success: false; data?: { message?: string } }).data;
        setUploadFout(errData?.message ?? 'Upload mislukt. Probeer het opnieuw.');
        return;
      }
      const d = (
        res as {
          success: true;
          data: { upload_id: number; ai_analyse: FotoData['aiAnalyse'] };
        }
      ).data;
      updateStap('foto', {
        ...wizardState.foto,
        uploadId: d.upload_id,
        aiAnalyse: d.ai_analyse,
        gebruikHandmatig: false,
      });
    } catch (e) {
      setUploadFout((e as Error).message);
    } finally {
      setIsUploading(false);
    }
  }

  // Save step data to backend (non-blocking)
  async function saveStap(stapNummer: number, data: unknown) {
    if (!wizardState.assessmentId) return;
    const nonce = wizardState.assessmentNonce || config.nonce;
    try {
      await ajaxPost(config.ajaxUrl, nonce, 'laadcheck_save_step', {
        assessment_id: wizardState.assessmentId,
        stap_nummer: stapNummer,
        data,
      });
    } catch {
      // Non-blocking — wizard continues regardless
    }
  }

  // Step 6: assessment completed — trigger report generation directly (payment already done).
  async function handleAfronden() {
    if (!wizardState.assessmentId) return;
    setIsLoading(true);
    setFout(null);
    try {
      await saveStap(6, {
        meterkast: wizardState.meterkast,
        woning: wizardState.woning,
        apparaten: wizardState.apparaten,
        laadwensen: wizardState.laadwensen,
      });

      await ajaxPost<{ success: boolean }>(
        config.ajaxUrl,
        wizardState.assessmentNonce || config.nonce,
        'laadcheck_complete_assessment',
        {
          assessment_id: wizardState.assessmentId,
          order_token: wizardState.orderToken,
        }
      );

      try {
        localStorage.removeItem(LS_WIZARD_KEY);
      } catch {
        // ignore
      }

      // Navigate to success screen (step 7).
      goTo(7);
    } catch (e) {
      setFout((e as Error).message);
      setIsLoading(false);
    }
  }

  return (
    <WizardLayout huidigeStap={huidigeStap}>
      {fout && (
        <div className="lc-fout-banner" role="alert">
          <strong>Er ging iets mis:</strong> {fout}
        </div>
      )}

      {huidigeStap === 1 && (
        <StapAccount
          state={wizardState.account}
          onChange={(v: AccountData) => updateStap('account', v)}
          onNext={async () => {
            await handleAccountNext();
            trackAssessmentStepCompleted(1);
          }}
          isLoading={isLoading}
        />
      )}

      {huidigeStap === 2 && !toonHandmatig && (
        <StapFoto
          state={foto}
          onChange={(v: FotoData) => updateStap('foto', v)}
          onNext={async () => {
            await saveStap(2, { foto: wizardState.foto });
            trackAssessmentStepCompleted(2);
            afterStepEdit(3);
          }}
          onBack={() => returnToSamenvatting ? handleReturnToSamenvatting() : goTo(1)}
          onUpload={handleFotoUpload}
          isUploading={isUploading}
          uploadError={uploadFout}
          returnToSamenvatting={returnToSamenvatting}
          onReturnToSamenvatting={handleReturnToSamenvatting}
        />
      )}

      {huidigeStap === 2 && toonHandmatig && (
        <StapHandmatig
          state={wizardState.meterkast}
          onChange={(v: MeterkastData) => updateStap('meterkast', v)}
          onNext={async () => {
            await saveStap(2, { meterkast: wizardState.meterkast });
            trackAssessmentStepCompleted(2);
            afterStepEdit(3);
          }}
          onBack={() => updateStap('foto', { ...foto, gebruikHandmatig: false })}
          returnToSamenvatting={returnToSamenvatting}
          onReturnToSamenvatting={handleReturnToSamenvatting}
        />
      )}

      {huidigeStap === 3 && (
        <StapWoning
          state={wizardState.woning}
          onChange={(v: WoningData) => updateStap('woning', v)}
          onNext={async () => {
            await saveStap(3, wizardState.woning);
            trackAssessmentStepCompleted(3);
            afterStepEdit(4);
          }}
          onBack={() => returnToSamenvatting ? handleReturnToSamenvatting() : goTo(2)}
          returnToSamenvatting={returnToSamenvatting}
          onReturnToSamenvatting={handleReturnToSamenvatting}
        />
      )}

      {huidigeStap === 4 && (
        <StapApparaten
          state={wizardState.apparaten}
          onChange={(v: ApparatenData) => updateStap('apparaten', v)}
          onNext={async () => {
            await saveStap(4, wizardState.apparaten);
            trackAssessmentStepCompleted(4);
            afterStepEdit(5);
          }}
          onBack={() => returnToSamenvatting ? handleReturnToSamenvatting() : goTo(3)}
          returnToSamenvatting={returnToSamenvatting}
          onReturnToSamenvatting={handleReturnToSamenvatting}
        />
      )}

      {huidigeStap === 5 && (
        <StapLaadwensen
          state={wizardState.laadwensen}
          onChange={(v: LaadwensenData) => updateStap('laadwensen', v)}
          onNext={async () => {
            await saveStap(5, wizardState.laadwensen);
            trackAssessmentStepCompleted(5);
            afterStepEdit(6);
          }}
          onBack={() => returnToSamenvatting ? handleReturnToSamenvatting() : goTo(4)}
          returnToSamenvatting={returnToSamenvatting}
          onReturnToSamenvatting={handleReturnToSamenvatting}
        />
      )}

      {huidigeStap === 6 && (
        <StapSamenvatting
          state={wizardState}
          onMeterkastChange={(v: MeterkastData) => updateStap('meterkast', v)}
          onGoToStap={goToFromSamenvatting}
          onAfronden={async () => {
            trackAssessmentStepCompleted(6);
            trackAssessmentCompleted();
            await handleAfronden();
          }}
          onBack={() => goTo(5)}
          isLoading={isLoading}
        />
      )}

      {huidigeStap === 7 && (
        <div className="lc-stap lc-stap--succes">
          <div className="lc-stap__succes-icoon" aria-hidden="true">✓</div>
          <h2 className="lc-stap__titel">Rapport wordt aangemaakt!</h2>
          <p className="lc-stap__beschrijving">
            Bedankt! Je persoonlijke laadcheck rapport wordt nu gegenereerd en verstuurd naar je e-mailadres.
            Controleer ook je spammap als je het niet snel ontvangt.
          </p>
        </div>
      )}
    </WizardLayout>
  );
}
